import { useEffect, useRef, useState } from 'react';

import { dayjs, Equipment, FORMAT_DATE_API } from '@umanforge/common';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { WarningIcon } from 'components/icons/warning_icon.component';
import { PostEquipment } from 'interfaces/api_equipment.interfaces';

interface ValidityDateProps {
  equipment: Equipment | PostEquipment;
  onChange: ( expirationDate: string ) => void;
  label?: string;
  icon?: React.SVGProps<SVGSVGElement>;
  required?: boolean;
  disabled?: boolean;
}


export function ExpirationDate( { equipment, onChange, label, icon, required, disabled }: ValidityDateProps ) {
  const { t } = useTranslation();
  const input = useRef( null );


  const [ isFocus, setIsFocus ] = useState<boolean>( false );
  const [ expirtationDate, setExpirtationDate ] = useState( equipment.expirationDate ? dayjs( equipment.expirationDate ) : dayjs().add( 1, 'days' ).startOf( 'day' ) );
  const [ isExpired, setIsExpired ] = useState( ( equipment.expirationDate ? dayjs( equipment.expirationDate ) : dayjs().add( 1, 'days' ).startOf( 'day' ) ).isBefore( dayjs(), 'date' ) );

  useEffect( () => {
    setIsExpired( expirtationDate.isBefore( dayjs(), 'date' ) );
  }, [ expirtationDate ] );

  return (
    <div className='flex flex-col gap-[2px] w-full'>
      <p className='flex gap-1 ml-1 items-center text-base'>
        {label}
      </p>
      <div className='relative'>
        <div
          className={
            'flex h-9 rounded-lg py-1 outline min-w-[150px] ' +
            ( icon ? 'pr-2 ' : 'px-2 ' ) +
            ( isFocus ? 'outline-2 outline-gray-base-dark bg-off-white ' : 'outline-1 outline-gray-base ' ) +
            ( disabled ? 'bg-stone-100 text-gray-600' : 'hover:outline-gray-base-dark hover:bg-off-white text-black' )
          }
        >
          {
            icon ?
              <div className='flex items-center my-0.5 mx-2 pr-2 select-none border-r'>
                { icon as JSX.Element }
              </div>
              :
              null
          }
          <div className='w-full font-semibold text-[12px]'>
            <div className={ 'flex ' + ( isExpired ? 'rounded-md bg-red-400 -my-[2px]' : '' ) }>
              <input
                required={ required }
                disabled={ disabled }
                id={ 'expirationDate-' + ( ( equipment as Equipment ).id ?? 'new-equipment' )}
                type="date"
                className={
                  'w-full pl-1 py-1 rounded-md cursor-pointer select-none ' +
                  ( isExpired ? 'border-[2px] border-red-400' : '' )
                }
                ref={ input }
                onClick={ () => {
                  if ( input.current ) {
                    ( input.current as HTMLInputElement ).showPicker();
                  }
                } }
                value={ expirtationDate.format( FORMAT_DATE_API ) }
                onChange={ e => {
                  const newDate = dayjs( e.target.value, FORMAT_DATE_API );
                  const oldDate = dayjs( e.target.defaultValue, FORMAT_DATE_API );
                  const difference = newDate.diff( oldDate, 'month', true );
                  setExpirtationDate( newDate );
                  if ( !Number.isInteger( difference ) || difference == 0 ) {
                    onChange( newDate.format( FORMAT_DATE_API ) );
                  }
                } }
                onBlur={ e => {
                  const newDate = dayjs( e.target.value, FORMAT_DATE_API );
                  if ( !newDate.isSame( dayjs( equipment.expirationDate ) ) ) {
                    setExpirtationDate( newDate );
                    onChange( newDate.format( FORMAT_DATE_API ) );
                  }
                  setIsFocus( false );
                } }
                onFocus={ () => { setIsFocus( !disabled && true ); } }
              />
              {
                isExpired ?
                  <>
                    <div
                      className='flex items-center justify-center w-8 h-8 px-1'
                      id={ 'expiredEquipment-' + ( ( equipment as Equipment ).id ?? 'new-equipment' )}
                    >
                      <WarningIcon className='w-full h-full fill-white'/>
                    </div>
                    <Tooltip
                      anchorSelect={ '#expiredEquipment-' + ( ( equipment as Equipment ).id ?? 'new-equipment' )}
                      className='z-[1000] max-w-[600px] text-wrap'
                      place="top"
                      positionStrategy='fixed'
                    >
                      <span>
                        <span className='text-[13px] font-semibold'> { t( 'equipment_expired' ) } </span>
                      </span>
                    </Tooltip>
                  </>
                  :
                  null
              }
            </div>
            <Tooltip
              anchorSelect={ '#expirationDate-' + ( ( equipment as Equipment ).id ?? 'new-equipment' )}
              className='z-[1000] max-w-[600px] text-wrap'
              place="top"
              positionStrategy='fixed'
            >
              <span className='flex gap-1 items-center'>
                <span className='text-[13px] font-semibold'> { t( 'expiration_date' ) } </span>
                <p> { t( '(date_included)' ) } </p>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>
      {
        isExpired ?
          <p className='h-[12px] text-[10px] text-error'>
            { t( 'expiration_date_is_expired' ) }
          </p>
          :
          null
      }
    </div>
  );
}
